/* ==========================================================================
   BANCO — Moteur de rendu "gravure laser" (Canvas 2D, zéro dépendance)

   Pipeline : niveaux de gris (luma Rec.709) → courbe de contraste en S →
   netteté (unsharp) → tramage (dithering Atkinson / Floyd / Bayer) →
   composition sur la photo produit (multiply + morsure + relief).

   S'appuie sur l'image DÉJÀ DÉTOURÉE par l'IA du configurateur existant
   (PNG à fond transparent) : les pixels transparents ne sont jamais gravés.

   Usage minimal :
     const engine = new BancoEngravingEngine();
     engine.setSource(imageDetouree);              // <img>, ImageBitmap ou canvas
     engine.compositeOnProduct(canvasApercu, photoProduit,
       { x: 220, y: 140, width: 400, rotation: 0 });

   Voir « 03 - Spécification moteur gravure.md » pour l'intégration complète.
   ========================================================================== */
(function (global) {
  'use strict';

  /* Matrice de Bayer 8×8 (tramage ordonné, O(n), pour le mode interactif) */
  var BAYER_8 = [
     0, 32,  8, 40,  2, 34, 10, 42,
    48, 16, 56, 24, 50, 18, 58, 26,
    12, 44,  4, 36, 14, 46,  6, 38,
    60, 28, 52, 20, 62, 30, 54, 22,
     3, 35, 11, 43,  1, 33,  9, 41,
    51, 19, 59, 27, 49, 17, 57, 25,
    15, 47,  7, 39, 13, 45,  5, 37,
    63, 31, 55, 23, 61, 29, 53, 21
  ];

  function BancoEngravingEngine(options) {
    this.params = Object.assign({
      previewMaxSize: 1000,     // px — borne du canvas d'aperçu
      mode: 'photo',            // 'photo' | 'trait' (logo : encre déjà binaire)
      contrast: 0.35,           // 0..1 — force de la courbe en S
      brightness: 0,            // -0.5..+0.5
      sharpen: 0.45,            // 0..1 — accentuation des détails
      /* Mélange des canaux pour le passage en gris. null = Rec.709.
         Sur un portrait, Rec.709 ne donne au rouge que 0,21 : un pull bordeaux
         ou une peau mate tombaient à 45/255 et gravaient en masse noire. Le
         filtre orange de l'argentique ([0.42, 0.44, 0.14]) les remet à leur
         clarté perçue. */
      lumaMix: null,
      autoNiveaux: true,        // étirement d'histogramme
      clip: 0.005,              // écrêtage de chaque côté
      plancher: 0.02,           // le noir ne descend pas à zéro : sans ce
                                // plancher, cheveux et vêtements sombres se
                                // soudent en une seule tache
      contrasteLocal: 0,        // 0..1 — masque flou de grand rayon : c'est ce
                                // qui fait réapparaître les plis d'un costume
                                // noir et le poil d'un chien foncé
      rayonLocal: 0.05,         // rayon, en fraction du petit côté
      encreCible: 0,            // 0 = désactivé. Densité d'encre visée sur le
                                // sujet : rattrape aussi bien la photo trop
                                // pâle que la photo bouchée
      grain: 1,                 // 0..1 — fibre du bois et lignes de balayage
      render: 'tone',           // 'tone' : brûlure continue (fidèle au rendu machine)
      toneGamma: 0.85,          // <1 : les tons moyens gravent plus franchement
      toneKnee: 0.30,           // seuil de blanc : en dessous, bois totalement nu
                                // 'dither' : trame visible (préparation production)
      dither: 'atkinson',       // 'atkinson' | 'floyd' | 'bayer' | 'none'
      invert: false,            // pour graver "en négatif"
      burnColor: [43, 24, 12],  // brun brûlé du bois gravé (RGB, mode dither)
      burnLight: [133, 86, 40], // mode tone : caramel des brûlures légères
      burnDark: [26, 14, 7],    // mode tone : expresso des brûlures profondes
      burnAlphaMin: 0.55,       // brûlure des zones claires
      burnAlphaMax: 0.95,       // brûlure des zones sombres
      bleed: 0.5,               // px — flou "morsure" du laser
      relief: true              // ombre interne subtile (profondeur)
    }, options || {});
    this._source = null;        // canvas pleine résolution de l'image détourée
    this._interactive = false;  // true pendant un drag → qualité rapide
  }

  BancoEngravingEngine.prototype = {

    /* image : <img> chargé, ImageBitmap ou canvas — PNG détouré de préférence */
    setSource: function (image) {
      var c = document.createElement('canvas');
      c.width = image.naturalWidth || image.width;
      c.height = image.naturalHeight || image.height;
      c.getContext('2d').drawImage(image, 0, 0);
      this._source = c;
    },

    setParams: function (partial) { Object.assign(this.params, partial); },

    /* Applique les réglages déduits par banco-image-analysis.js. C'est le point
       de jonction entre « ce qu'on a reconnu » et « comment on grave ». */
    appliquerAnalyse: function (rapport) {
      if (!rapport || !rapport.gravure) return this;
      var r = rapport.gravure;
      this.setParams({
        mode: r.mode,
        lumaMix: r.lumaMix,
        autoNiveaux: !!r.autoNiveaux,
        clip: r.clip === undefined ? 0.005 : r.clip,
        plancher: r.plancher || 0,
        contrasteLocal: r.contrasteLocal || 0,
        rayonLocal: r.rayonLocal || 0.05,
        encreCible: r.encreCible || 0,
        sharpen: r.netiquete === undefined ? this.params.sharpen : r.netiquete,
        toneKnee: r.knee === undefined ? this.params.toneKnee : r.knee,
        toneGamma: r.gamma === undefined ? this.params.toneGamma : r.gamma,
        /* un logo se grave net : ni fibre de bois marquée, ni morsure large,
           sinon les fines lettres bavent */
        grain: r.mode === 'trait' ? 0.25 : 1,
        bleed: r.mode === 'trait' ? 0.25 : 0.5
      });
      return this;
    },

    /* À activer pendant la manipulation d'un curseur/drag (rendu Bayer rapide),
       puis désactiver et relancer un rendu pour la qualité finale. */
    setInteractive: function (on) { this._interactive = !!on; },

    /* ---------------------- Étapes internes ---------------------- */

    _rasterize: function (maxDim) {
      var src = this._source;
      if (!src) throw new Error('BancoEngravingEngine : appeler setSource() d’abord.');
      var scale = Math.min(1, maxDim / Math.max(src.width, src.height));
      /* autoriser l'upscale pour l'export production */
      if (maxDim > Math.max(src.width, src.height)) scale = maxDim / Math.max(src.width, src.height);
      var w = Math.max(1, Math.round(src.width * scale));
      var h = Math.max(1, Math.round(src.height * scale));
      var c = document.createElement('canvas');
      c.width = w; c.height = h;
      var ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(src, 0, 0, w, h);
      return ctx.getImageData(0, 0, w, h);
    },

    /* Luminance Rec.709 par défaut, ou mélange de canaux choisi par l'analyse
       (filtre orange pour les carnations) — jamais une désaturation plate */
    _toGray: function (imgData) {
      var d = imgData.data, w = imgData.width, h = imgData.height;
      var mix = this.params.lumaMix;
      var kr = mix ? mix[0] : 0.2126, kv = mix ? mix[1] : 0.7152, kb = mix ? mix[2] : 0.0722;
      var somme = kr + kv + kb;
      if (somme > 0 && Math.abs(somme - 1) > 0.001) { kr /= somme; kv /= somme; kb /= somme; }
      var gray = new Float32Array(w * h);
      var alpha = new Uint8ClampedArray(w * h);
      for (var i = 0, p = 0; i < d.length; i += 4, p++) {
        gray[p] = (kr * d[i] + kv * d[i + 1] + kb * d[i + 2]) / 255;
        alpha[p] = d[i + 3];
      }
      return { gray: gray, alpha: alpha, w: w, h: h };
    },

    /* Moyenne locale ne tenant compte QUE du sujet (sommes courantes, O(n)).
       Un flou ordinaire aspirerait les pixels transparents — noirs — et
       cernerait le sujet d'un halo clair au moment du contraste local. */
    _flouMasque: function (gray, alpha, w, h, r) {
      var n = w * h;
      var num = new Float32Array(n), den = new Float32Array(n);
      var p, x, y, m;
      for (p = 0; p < n; p++) {
        m = alpha[p] >= 128 ? 1 : 0;
        num[p] = gray[p] * m; den[p] = m;
      }
      var passe = function (src, w2, h2) {
        var t = new Float32Array(w2 * h2), s, i, k;
        for (y = 0; y < h2; y++) {
          s = 0;
          for (i = 0; i <= r && i < w2; i++) s += src[y * w2 + i];
          for (x = 0; x < w2; x++) {
            t[y * w2 + x] = s;
            k = x - r; if (k >= 0) s -= src[y * w2 + k];
            k = x + r + 1; if (k < w2) s += src[y * w2 + k];
          }
        }
        var o = new Float32Array(w2 * h2);
        for (x = 0; x < w2; x++) {
          s = 0;
          for (i = 0; i <= r && i < h2; i++) s += t[i * w2 + x];
          for (y = 0; y < h2; y++) {
            o[y * w2 + x] = s;
            k = y - r; if (k >= 0) s -= t[k * w2 + x];
            k = y + r + 1; if (k < h2) s += t[k * w2 + x];
          }
        }
        return o;
      };
      var sn = passe(num, w, h), sd = passe(den, w, h);
      var out = new Float32Array(n);
      for (p = 0; p < n; p++) out[p] = sd[p] > 0.5 ? sn[p] / sd[p] : gray[p];
      return out;
    },

    /* Contraste local : g + (g − moyenne locale) × force. C'est LE réglage qui
       sépare une gravure plate d'une gravure qui « respire » — le costume noir
       reprend ses plis, le poil du chien redevient du poil. */
    _contrasteLocal: function (gray, alpha, w, h) {
      var P = this.params;
      if (!P.contrasteLocal) return;
      var r = Math.max(2, Math.round(Math.min(w, h) * P.rayonLocal));
      var base = this._flouMasque(gray, alpha, w, h, r);
      var f = P.contrasteLocal, v;
      for (var p = 0; p < gray.length; p++) {
        if (alpha[p] < 128) continue;
        v = gray[p] + (gray[p] - base[p]) * f;
        gray[p] = v < 0 ? 0 : v > 1 ? 1 : v;
      }
    },

    /* Auto-exposition : ajuste la densité d'encre du sujet vers la cible, par
       une puissance sur l'encre (bisection sur l'histogramme, 14 itérations).
       Sans elle, un chien crème gravait en fantôme et un costume noir en pavé.
       Correction volontairement bridée : on rattrape, on ne normalise pas tout
       au même gris — une silhouette à contre-jour doit rester une silhouette. */
    _exposition: function (gray, alpha) {
      var cible = this.params.encreCible;
      if (!cible) return;
      var histo = new Float64Array(101), n = 0, p, b;
      for (p = 0; p < gray.length; p++) {
        if (alpha[p] < 128) continue;
        b = ((1 - gray[p]) * 100 + 0.5) | 0;
        histo[b]++; n++;
      }
      if (n < 64) return;
      var moyenne = function (puiss) {
        var s = 0;
        for (var i = 0; i <= 100; i++) {
          if (!histo[i]) continue;
          s += histo[i] * Math.pow(i / 100, puiss);
        }
        return s / n;
      };
      if (moyenne(1) < 0.004) return; /* image quasi blanche : rien à doser */
      var lo = 0.55, hi = 1.9, mid = 1;
      for (var k = 0; k < 14; k++) {
        mid = (lo + hi) / 2;
        if (moyenne(mid) > cible) lo = mid; else hi = mid;
      }
      var puiss = 1 + (mid - 1) * 0.75; /* correction adoucie */
      if (Math.abs(puiss - 1) < 0.02) return;
      for (p = 0; p < gray.length; p++) {
        if (alpha[p] < 128) continue;
        gray[p] = 1 - Math.pow(1 - gray[p], puiss);
      }
    },

    /* Auto-niveaux : étire l'histogramme (écrêtage 0,5 % de chaque côté).
       Indispensable : même une photo terne ou sous-exposée grave avec du
       punch — c'est ce que fait tout logiciel de gravure sérieux. */
    _autoLevels: function (gray, alpha) {
      var P = this.params;
      if (!P.autoNiveaux) return;
      var hist = new Uint32Array(256), n = 0, i, v;
      for (i = 0; i < gray.length; i++) {
        if (alpha[i] < 128) continue;
        hist[(gray[i] * 255 + 0.5) | 0]++;
        n++;
      }
      if (!n) return;
      var clip = Math.max(1, Math.round(n * (P.clip === undefined ? 0.005 : P.clip)));
      var lo = 0, hi = 255, acc = 0;
      for (i = 0; i < 256; i++) { acc += hist[i]; if (acc >= clip) { lo = i; break; } }
      acc = 0;
      for (i = 255; i >= 0; i--) { acc += hist[i]; if (acc >= clip) { hi = i; break; } }
      if (hi - lo < 8) return; /* image quasi uniforme : ne pas amplifier le bruit */
      /* le plancher empêche l'écrasement des ombres : sur un portrait, mapper
         le point noir sur 0 soude les cheveux, les sourcils et le vêtement */
      var sol = P.plancher || 0;
      var lo01 = lo / 255, range = (hi - lo) / 255;
      for (i = 0; i < gray.length; i++) {
        if (alpha[i] < 128) continue;
        v = (gray[i] - lo01) / range;
        v = v < 0 ? 0 : v > 1 ? 1 : v;
        gray[i] = sol + v * (1 - sol);
      }
    },

    /* Courbe en S (smootherstep) mélangée à l'identité selon params.contrast,
       + luminosité, + inversion optionnelle. Appliquée via LUT 256 entrées. */
    _applyCurve: function (gray) {
      var P = this.params;
      var lut = new Float32Array(256);
      for (var i = 0; i < 256; i++) {
        var v = i / 255 + P.brightness;
        v = Math.min(1, Math.max(0, v));
        var s = v * v * v * (v * (v * 6 - 15) + 10);
        v = v + (s - v) * P.contrast;
        if (P.invert) v = 1 - v;
        lut[i] = v;
      }
      for (var p = 0; p < gray.length; p++) {
        gray[p] = lut[(gray[p] * 255 + 0.5) | 0];
      }
    },

    /* Unsharp mask léger : g + (g - flou3×3(g)) × amount×2 */
    _sharpen: function (gray, w, h) {
      var amount = this.params.sharpen;
      if (!amount) return;
      var out = new Float32Array(gray.length);
      for (var y = 0; y < h; y++) {
        for (var x = 0; x < w; x++) {
          var sum = 0, n = 0;
          for (var dy = -1; dy <= 1; dy++) {
            var yy = y + dy;
            if (yy < 0 || yy >= h) continue;
            for (var dx = -1; dx <= 1; dx++) {
              var xx = x + dx;
              if (xx < 0 || xx >= w) continue;
              sum += gray[yy * w + xx]; n++;
            }
          }
          var g = gray[y * w + x];
          out[y * w + x] = Math.min(1, Math.max(0, g + (g - sum / n) * amount * 2));
        }
      }
      gray.set(out);
    },

    /* Tramage → Uint8Array (1 = pixel gravé). Pixels transparents jamais gravés. */
    _dither: function (gray, alpha, w, h) {
      var mode = this._interactive ? 'bayer' : this.params.dither;
      var out = new Uint8Array(w * h);
      var p, x, y;

      if (mode === 'none') {
        for (p = 0; p < gray.length; p++) out[p] = (alpha[p] >= 128 && gray[p] < 0.5) ? 1 : 0;
        return out;
      }

      if (mode === 'bayer') {
        for (y = 0; y < h; y++) {
          for (x = 0; x < w; x++) {
            p = y * w + x;
            if (alpha[p] < 128) continue;
            out[p] = gray[p] < (BAYER_8[(y & 7) * 8 + (x & 7)] + 0.5) / 64 ? 1 : 0;
          }
        }
        return out;
      }

      /* Diffusion d'erreur — copie de travail pour ne pas polluer `gray` */
      var g = Float32Array.from(gray);
      function spread(x2, y2, e) {
        if (x2 < 0 || x2 >= w || y2 < 0 || y2 >= h) return;
        var q = y2 * w + x2;
        if (alpha[q] >= 128) g[q] += e;
      }

      if (mode === 'floyd') {
        /* Floyd–Steinberg serpentin (limite les artefacts en "vagues") */
        for (y = 0; y < h; y++) {
          var ltr = (y % 2 === 0);
          for (var k = 0; k < w; k++) {
            x = ltr ? k : (w - 1 - k);
            p = y * w + x;
            if (alpha[p] < 128) continue;
            var oldF = g[p], newF = oldF < 0.5 ? 0 : 1;
            out[p] = newF === 0 ? 1 : 0;
            var errF = oldF - newF;
            var dir = ltr ? 1 : -1;
            spread(x + dir, y, errF * 7 / 16);
            spread(x - dir, y + 1, errF * 3 / 16);
            spread(x, y + 1, errF * 5 / 16);
            spread(x + dir, y + 1, errF * 1 / 16);
          }
        }
        return out;
      }

      /* Atkinson (défaut) : ne diffuse que 6/8 de l'erreur → hautes lumières
         préservées, rendu "gravure" plus propre sur bois clair. */
      for (y = 0; y < h; y++) {
        for (x = 0; x < w; x++) {
          p = y * w + x;
          if (alpha[p] < 128) continue;
          var oldA = g[p], newA = oldA < 0.5 ? 0 : 1;
          out[p] = newA === 0 ? 1 : 0;
          var e = (oldA - newA) / 8;
          spread(x + 1, y, e);
          spread(x + 2, y, e);
          spread(x - 1, y + 1, e);
          spread(x, y + 1, e);
          spread(x + 1, y + 1, e);
          spread(x, y + 2, e);
        }
      }
      return out;
    },

    /* ---------------------- API publique ---------------------- */

    /* Canvas RGBA de la gravure seule (brun brûlé sur fond transparent).
       En mode interactif : résolution réduite + Bayer → < 16 ms sur mobile. */
    renderEngraving: function (maxDim) {
      var size = maxDim || this.params.previewMaxSize;
      if (this._interactive) size = Math.round(size / 1.5);
      var img = this._rasterize(size);
      var g = this._toGray(img);
      var P = this.params;

      /* Mode « trait » : l'encre du logo a déjà été extraite exactement par
         l'analyse (banco-image-analysis.js). Auto-niveaux, courbe et contraste
         local n'auraient rien à corriger et ne feraient que salir les aplats. */
      if (P.mode !== 'trait') {
        this._autoLevels(g.gray, g.alpha);
        this._contrasteLocal(g.gray, g.alpha, g.w, g.h);
        this._applyCurve(g.gray);
        if (!this._interactive) this._sharpen(g.gray, g.w, g.h);
        this._exposition(g.gray, g.alpha);
      }

      var out = new ImageData(g.w, g.h);
      var p, i, a;

      if (P.render === 'tone') {
        /* Brûlure continue bi-ton, comme les vraies gravures BANCO :
           - les brûlures légères tirent vers le caramel doré,
           - les profondes vers l'expresso presque noir,
           - fines lignes de balayage horizontales (la tête laser
             grave ligne par ligne) + grain stable façon fibre de bois. */
        var light = P.burnLight, dark = P.burnDark;
        var grain = P.grain === undefined ? 1 : P.grain;
        var t, row, hsh, couv;
        for (p = 0; p < g.gray.length; p++) {
          /* couverture partielle conservée : les bords anti-crénelés d'un logo
             et la plume d'un masque nettoyé se fondent au lieu d'être coupés
             net à 128 comme avant */
          if (g.alpha[p] < 8) continue;
          couv = g.alpha[p] / 255;
          t = Math.pow(1 - g.gray[p], P.toneGamma);
          /* seuil doux : les hautes lumières restent du bois totalement nu,
             comme sur une vraie gravure (pas de voile sépia sur les blancs) */
          t = (t - P.toneKnee) / (1 - P.toneKnee);
          if (t <= 0) continue;
          a = Math.pow(t, 0.9) * 0.97 * couv;
          row = (p / g.w) | 0;
          hsh = ((p * 2654435761) >>> 24) / 255; /* pseudo-aléa stable par pixel */
          a *= 1 + grain * ((row & 1 ? 0.93 : 1) * (0.88 + 0.24 * hsh) - 1);
          if (a > 1) a = 1;
          i = p * 4;
          out.data[i]     = (light[0] + (dark[0] - light[0]) * t + 0.5) | 0;
          out.data[i + 1] = (light[1] + (dark[1] - light[1]) * t + 0.5) | 0;
          out.data[i + 2] = (light[2] + (dark[2] - light[2]) * t + 0.5) | 0;
          out.data[i + 3] = (a * 255 + 0.5) | 0;
        }
        var cT = document.createElement('canvas');
        cT.width = g.w; cT.height = g.h;
        cT.getContext('2d').putImageData(out, 0, 0);
        return cT;
      }

      var map = this._dither(g.gray, g.alpha, g.w, g.h);
      for (p = 0; p < map.length; p++) {
        if (!map[p]) continue;
        i = p * 4;
        /* zones sombres = brûlure plus marquée → richesse tonale */
        a = P.burnAlphaMin + (P.burnAlphaMax - P.burnAlphaMin) * (1 - g.gray[p]);
        out.data[i] = P.burnColor[0];
        out.data[i + 1] = P.burnColor[1];
        out.data[i + 2] = P.burnColor[2];
        out.data[i + 3] = (a * 255 + 0.5) | 0;
      }
      var c = document.createElement('canvas');
      c.width = g.w; c.height = g.h;
      c.getContext('2d').putImageData(out, 0, 0);
      return c;
    },

    /* Aperçu final : gravure incrustée sur la photo studio du produit.
       zone : { x, y, width, rotation } en px du canvas cible
       (hauteur déduite du ratio de l'image source). */
    compositeOnProduct: function (targetCanvas, productImage, zone) {
      var ctx = targetCanvas.getContext('2d');
      ctx.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
      ctx.drawImage(productImage, 0, 0, targetCanvas.width, targetCanvas.height);

      var engraving = this.renderEngraving();
      var hgt = zone.width * engraving.height / engraving.width;

      ctx.save();
      ctx.translate(zone.x + zone.width / 2, zone.y + hgt / 2);
      ctx.rotate((zone.rotation || 0) * Math.PI / 180);
      if (this.params.relief) {
        /* relief : légère ombre portée vers le bas = creux perçu */
        ctx.shadowColor = 'rgba(0,0,0,0.30)';
        ctx.shadowBlur = 1.2;
        ctx.shadowOffsetY = 0.8;
      }
      /* multiply : la texture du bois transparaît à travers la brûlure */
      ctx.globalCompositeOperation = 'multiply';
      if (this.params.bleed && 'filter' in ctx) {
        ctx.filter = 'blur(' + this.params.bleed + 'px)';
      }
      ctx.drawImage(engraving, -zone.width / 2, -hgt / 2, zone.width, hgt);
      ctx.restore();
      return targetCanvas;
    },

    /* Garde-fou qualité — à appeler après l'upload et à chaque redimensionnement.
       engraveWidthMM : largeur physique de la gravure choisie par le client. */
    analyzeQuality: function (engraveWidthMM) {
      var messages = [];
      var pxPerMM = this._source.width / engraveWidthMM;
      var resolutionOk = pxPerMM >= 4; /* ≈ 100 dpi minimum utile sur bois */
      if (!resolutionOk) {
        messages.push('Cette photo risque de manquer de détails une fois gravée. Essayez une photo plus grande, ou réduisez la taille de la gravure.');
      }
      var img = this._rasterize(400);
      var g = this._toGray(img);
      var sum = 0, sum2 = 0, n = 0, min = 1, max = 0;
      for (var p = 0; p < g.gray.length; p++) {
        if (g.alpha[p] < 128) continue;
        var v = g.gray[p];
        sum += v; sum2 += v * v; n++;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      var mean = n ? sum / n : 0;
      var std = n ? Math.sqrt(Math.max(0, sum2 / n - mean * mean)) : 0;
      var contrastOk = std >= 0.15 && (max - min) >= 0.5;
      if (n && !contrastOk) {
        messages.push('Cette photo est peu contrastée : le rendu gravé peut paraître plat. Essayez une photo plus contrastée, ou augmentez le curseur de contraste.');
      }
      return { resolutionOk: resolutionOk, contrastOk: contrastOk, pxPerMM: pxPerMM, std: std, messages: messages };
    },

    /* Fichier de production pour l'atelier — DISTINCT de l'aperçu web.
       widthMM : largeur physique gravée. dpi : résolution machine (254–508).
       mode 'dithered' (défaut) : 1 bit, strictement identique à l'aperçu (WYSIWYG).
       mode 'grayscale' : niveaux de gris, si le pilote laser trame lui-même.
       Retourne une Promise<Blob> PNG (noir sur blanc). */
    exportProduction: function (opts) {
      opts = opts || {};
      var widthMM = opts.widthMM || 100;
      var dpi = opts.dpi || 318;
      var mode = opts.mode || 'dithered';
      var self = this;

      return new Promise(function (resolve, reject) {
        var wasInteractive = self._interactive;
        self._interactive = false;

        var src = self._source;
        var widthPx = Math.round(widthMM / 25.4 * dpi);
        var maxDim = Math.round(widthPx * Math.max(src.width, src.height) / src.width);

        var img = self._rasterize(maxDim);
        var g = self._toGray(img);
        self._autoLevels(g.gray, g.alpha);
        self._applyCurve(g.gray);
        self._sharpen(g.gray, g.w, g.h);

        var out = new ImageData(g.w, g.h);
        var p, i, v;
        if (mode === 'grayscale') {
          for (p = 0; p < g.gray.length; p++) {
            i = p * 4;
            v = g.alpha[p] < 128 ? 255 : (g.gray[p] * 255 + 0.5) | 0;
            out.data[i] = out.data[i + 1] = out.data[i + 2] = v;
            out.data[i + 3] = 255;
          }
        } else {
          var map = self._dither(g.gray, g.alpha, g.w, g.h);
          for (p = 0; p < map.length; p++) {
            i = p * 4;
            v = map[p] ? 0 : 255;
            out.data[i] = out.data[i + 1] = out.data[i + 2] = v;
            out.data[i + 3] = 255;
          }
        }

        var c = document.createElement('canvas');
        c.width = g.w; c.height = g.h;
        c.getContext('2d').putImageData(out, 0, 0);
        self._interactive = wasInteractive;
        c.toBlob(function (blob) {
          blob ? resolve(blob) : reject(new Error('Export PNG impossible.'));
        }, 'image/png');
      });
    }
  };

  global.BancoEngravingEngine = BancoEngravingEngine;
})(typeof window !== 'undefined' ? window : this);
